/**
 * useDefaultQuantity — the single source of truth for the default order size.
 *
 * Desktop order entry (FloatingOrderPanel -> ActionBarTrading -> useTradingLogic)
 * and mobile order entry (TradingPanel's bottom bar) both call this hook, so
 * there is exactly one stored value and one set of preset chips behind them.
 * It rides on the existing server-backed preferences blob
 * (`tradingPanel.defaultQuantity`), so it persists across reloads and devices
 * without a new storage layer.
 *
 * Note the split between `defaultQuantity` (what a fresh ticket starts at, and
 * what a position close returns to) and `tradingPanel.quantity` (the working
 * size the user has dialled in right now). They are deliberately separate: the
 * whole point of the default is that the working value can wander away from it
 * and still come home.
 */

import { useCallback, useMemo } from 'react';
import { usePreferences } from './usePreferences';
import {
  isValidOrderQuantity,
  quantityPresetsWith,
  sanitizeOrderQuantity,
} from '../config/orderQuantity';

export interface UseDefaultQuantityResult {
  /** Always a valid quantity, whatever junk is in storage. */
  defaultQuantity: number;
  /**
   * Persist a new default. Returns false and changes nothing when the value is
   * not a valid quantity, so a half-typed input can never clobber the setting.
   */
  setDefaultQuantity: (value: unknown) => boolean;
  /** Preset chips with the default folded in. */
  quantityPresets: number[];
}

export function useDefaultQuantity(): UseDefaultQuantityResult {
  const { preferences, updatePreference } = usePreferences();

  const defaultQuantity = sanitizeOrderQuantity(preferences.tradingPanel?.defaultQuantity);

  const setDefaultQuantity = useCallback(
    (value: unknown): boolean => {
      if (!isValidOrderQuantity(value)) return false;
      updatePreference('tradingPanel', { defaultQuantity: Number(value) });
      return true;
    },
    [updatePreference],
  );

  const quantityPresets = useMemo(() => quantityPresetsWith(defaultQuantity), [defaultQuantity]);

  return { defaultQuantity, setDefaultQuantity, quantityPresets };
}
